const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Format a Date as YYYY-MM-DD in the user's *local* timezone.
// `toISOString()` converts to UTC, which shifts the date by one day for users
// east of UTC after local midnight (e.g. IST late-night → previous day in UTC).
// Use this everywhere we feed `<input type="date">`, compare with backend
// date-strings (which are stored as IST-implicit YYYY-MM-DD), or set form
// defaults / `min` constraints.
export const toLocalISODate = (d = new Date()) => {
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Short weekday name (Sun/Mon/.../Sat) — used on attendance surfaces
// to give a quick "is this a weekend?" cue alongside the date column.
// Returns '' for invalid input so callers can `formatDay(d) && ...`.
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const formatDay = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return WEEKDAYS_SHORT[date.getDay()];
};

// Convenience: "21-Jun-2026 · Sun" — used by attendance tables that
// want both pieces inline with a separator. Mirrors `formatDate` for
// invalid input.
export const formatDateWithDay = (d) => {
  const date = formatDate(d);
  if (!date) return '';
  const day = formatDay(d);
  return day ? `${date} · ${day}` : date;
};

export const formatDateTime = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${day}-${month}-${year}, ${hours}:${minutes}${ampm}`;
};

/**
 * "1st", "2nd", "3rd", "4th", … "21st", "22nd", "23rd" — handles the 11/12/13
 * trio correctly (they're all "th", not "st"/"nd"/"rd").
 */
const _ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 * Compute the renewal-period string used on payment-link descriptions
 * and auto-generated tax invoices.
 *
 * @param {string} currentExpiryISO – ISO date string of the current
 *   expiry. Period STARTS the day AFTER this date (per accounting
 *   convention — renewals begin on day N+1 so there's no double-billed
 *   day).
 * @param {number} tenureYears – how many years the renewal covers.
 * @returns {string} – e.g. "1st Jul 2026 to 30th Jun 2029", or "" when
 *   the input is unparsable (caller short-circuits the second line).
 */
export const formatRenewalPeriod = (currentExpiryISO, tenureYears) => {
  if (!currentExpiryISO || !tenureYears) return '';
  const start = new Date(currentExpiryISO);
  if (isNaN(start.getTime())) return '';
  start.setDate(start.getDate() + 1);   // day AFTER current expiry
  // End date: same day-of-month, +N years, then BACK ONE day so a
  // 1-year renewal of "1 Jul 2026" lands on "30 Jun 2027" (correct
  // anniversary semantics, not "1 Jul 2027 -1 sec").
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + Number(tenureYears));
  end.setDate(end.getDate() - 1);
  const fmt = (d) =>
    `${_ordinal(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  return `${fmt(start)} to ${fmt(end)}`;
};

/**
 * Render just the time portion of a timestamp in 12-hour format with IST
 * suffix. Used as a muted sub-line under a date cell so same-day rows
 * are distinguishable at a glance. Returns "" on falsy / unparsable
 * input so the caller can short-circuit cleanly.
 *
 * Example: "2026-06-17T14:14:23+00:00" → "2:14 PM IST"
 */
export const formatTimeIST = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm} IST`;
};

// Compact relative-time formatter: "just now", "5m ago", "2h ago",
// "yesterday", "3d ago", or absolute date fallback for older items.
// Use for "last updated" chips and activity recency hints.
export const formatRelative = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 0) return 'just now';
  if (diffSec < 45) return 'just now';
  if (diffSec < 90) return '1m ago';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDate(date);
};



// Forward-looking countdown for renewal-style dates: "today", "tomorrow",
// "in 12 days", "expired 3 days ago". Returns the label + a urgency tier
// so the caller can color the cell consistently across Hosting / AMC etc.
export const formatDaysUntil = (d) => {
  if (!d) return { label: '', tier: 'unknown', days: null };
  const date = new Date(d);
  if (isNaN(date.getTime())) return { label: '', tier: 'unknown', days: null };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / 86400000);
  let label, tier;
  if (days < 0) { label = `expired ${-days} day${days === -1 ? '' : 's'} ago`; tier = 'expired'; }
  else if (days === 0) { label = 'today'; tier = 'critical'; }
  else if (days === 1) { label = 'tomorrow'; tier = 'critical'; }
  else if (days <= 7) { label = `in ${days} days`; tier = 'critical'; }
  else if (days <= 30) { label = `in ${days} days`; tier = 'warn'; }
  else { label = `in ${days} days`; tier = 'ok'; }
  return { label, tier, days };
};
